import React, { useState, useEffect, useRef } from 'react';
import { 
  Settings, 
  Save,
  Globe,
  MessageCircle,
  Mail,
  Loader,
  Upload,
  Image as ImageIcon
} from 'lucide-react';
import { AdminLayout } from '../../components/Admin/AdminLayout';
import { ToggleSwitch } from '../../components/Common/ToggleSwitch';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

interface Settings {
  site_name: string;
  site_url: string;
  support_email: string;
  pro_plan_price: number;
  allow_registration: boolean;
  require_email_verification: boolean;
  maintenance_mode: boolean;
  free_plan_page_limit: number;
  free_plan_pixel_limit: number;
  email_notifications_enabled: boolean;
  notify_on_new_user: boolean;
  notify_on_payment: boolean;
  admin_notification_email: string;
  telegram_notifications_enabled: boolean;
  telegram_bot_token: string;
  telegram_chat_id: string;
  logo_url: string;
  logo_text: string;
}

export const AdminSettings: React.FC = () => {
  const token = useAuthStore((state) => state.token);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [settings, setSettings] = useState<Settings>({
    site_name: '',
    site_url: '',
    support_email: '',
    pro_plan_price: 899,
    allow_registration: true,
    require_email_verification: false,
    maintenance_mode: false,
    free_plan_page_limit: 3,
    free_plan_pixel_limit: 1,
    email_notifications_enabled: true,
    notify_on_new_user: true,
    notify_on_payment: true,
    admin_notification_email: '',
    telegram_notifications_enabled: false,
    telegram_bot_token: '',
    telegram_chat_id: '',
    logo_url: '',
    logo_text: '',
  });

  // Load settings on mount
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }

      const data = await response.json();
      setSettings(data);
    } catch (error: any) {
      console.error('Fetch settings error:', error);
      toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload an image file.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setIsUploadingLogo(true);

    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await fetch(`${API_URL}/admin/upload-logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload logo');
      }

      const data = await response.json();
      setSettings({ ...settings, logo_url: data.url });
      toast.success('Logo uploaded successfully! 🎉');
    } catch (error: any) {
      console.error('Logo upload error:', error);
      toast.error(error.message || 'Failed to upload logo');
    } finally {
      setIsUploadingLogo(false);
      // Reset file input
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      const response = await fetch(`${API_URL}/admin/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
      });

      if (!response.ok) {
        throw new Error('Failed to save settings');
      }

      const data = await response.json();
      setSettings(data.settings);
      toast.success('Settings saved successfully! 🎉');
    } catch (error: any) {
      console.error('Save settings error:', error);
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Settings className="w-8 h-8 text-orange-600" />
              System Settings
            </h1>
            <p className="text-gray-600 mt-1">Configure system-wide settings</p>
          </div>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Save Changes
              </>
            )}
          </button>
        </div>

        {/* Settings Grid */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* General Settings */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <Globe className="w-6 h-6 text-blue-600" />
              <h3 className="text-xl font-bold text-gray-900">General Settings</h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Site Name</label>
                <input
                  type="text"
                  value={settings.site_name}
                  onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Site URL</label>
                <input
                  type="text"
                  value={settings.site_url}
                  onChange={(e) => setSettings({ ...settings, site_url: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Support Email</label>
                <input
                  type="email"
                  value={settings.support_email}
                  onChange={(e) => setSettings({ ...settings, support_email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>


          {/* Feature Settings */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <Settings className="w-6 h-6 text-orange-600" />
              <h3 className="text-xl font-bold text-gray-900">Feature Settings</h3>
            </div>
            
            <div className="space-y-4">
              <ToggleSwitch
                label="Allow New Registrations"
                checked={settings.allow_registration}
                onChange={(checked) => setSettings({ ...settings, allow_registration: checked })}
                description="Enable or disable new user signups"
              />
              
              <ToggleSwitch
                label="Require Email Verification"
                checked={settings.require_email_verification}
                onChange={(checked) => setSettings({ ...settings, require_email_verification: checked })}
                description="Users must verify email before access"
              />
              
              <ToggleSwitch
                label="Maintenance Mode"
                checked={settings.maintenance_mode}
                onChange={(checked) => setSettings({ ...settings, maintenance_mode: checked })}
                description="Disable site for maintenance"
              />
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <p className="text-sm text-blue-800 font-medium">💡 Plan Limits (Hardcoded)</p>
              <ul className="text-xs text-blue-700 mt-2 space-y-1">
                <li>• Free: 1 landing page, 1 FB pixel</li>
                <li>• Plus: 5 landing pages, 1 FB pixel, Device Redirect</li>
                <li>• Pro: Unlimited pages, Multiple pixels, Bot Cloaking</li>
              </ul>
              <p className="text-xs text-blue-600 mt-2">
                Plan limits are configured in code (planLimits.js)
              </p>
            </div>
          </div>

          {/* Email Notification Settings */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <Mail className="w-6 h-6 text-purple-600" />
              <h3 className="text-xl font-bold text-gray-900">Email Notifications</h3>
            </div>
            
            <div className="space-y-4">
              <ToggleSwitch
                label="Enable Email Notifications"
                checked={settings.email_notifications_enabled}
                onChange={(checked) => setSettings({ ...settings, email_notifications_enabled: checked })}
                description="Master switch for all email notifications"
              />

              {settings.email_notifications_enabled && (
                <>
                  <div className="ml-6 space-y-4 pt-2 border-l-2 border-gray-200 pl-4">
                    <ToggleSwitch
                      label="New User Alerts"
                      checked={settings.notify_on_new_user}
                      onChange={(checked) => setSettings({ ...settings, notify_on_new_user: checked })}
                      description="Get notified when new users sign up"
                    />
                    
                    <ToggleSwitch
                      label="Payment Alerts"
                      checked={settings.notify_on_payment}
                      onChange={(checked) => setSettings({ ...settings, notify_on_payment: checked })}
                      description="Get notified on new payments"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Admin Notification Email</label>
                    <input
                      type="email"
                      value={settings.admin_notification_email}
                      onChange={(e) => setSettings({ ...settings, admin_notification_email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="admin@hitzlink.com"
                    />
                    <p className="text-xs text-gray-500 mt-1">Email to receive admin notifications</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Telegram Notification Settings */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <MessageCircle className="w-6 h-6 text-blue-600" />
              <h3 className="text-xl font-bold text-gray-900">Telegram Notifications</h3>
            </div>
            
            <div className="space-y-4">
              <ToggleSwitch
                label="Enable Telegram Notifications"
                checked={settings.telegram_notifications_enabled}
                onChange={(checked) => setSettings({ ...settings, telegram_notifications_enabled: checked })}
                description="Send admin notifications to Telegram"
              />

              {settings.telegram_notifications_enabled && (
                <div className="grid md:grid-cols-2 gap-4 ml-6 pt-2 border-l-2 border-gray-200 pl-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telegram Bot Token
                    </label>
                    <input
                      type="text"
                      value={settings.telegram_bot_token}
                      onChange={(e) => setSettings({ ...settings, telegram_bot_token: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                      placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Get from <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">@BotFather</a>
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telegram Chat ID
                    </label>
                    <input
                      type="text"
                      value={settings.telegram_chat_id}
                      onChange={(e) => setSettings({ ...settings, telegram_chat_id: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                      placeholder="-1001234567890"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Your user ID or group chat ID
                    </p>
                  </div>
                </div>
              )}

              {settings.telegram_notifications_enabled && (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800 font-medium mb-2">📱 Setup Instructions:</p>
                    <ol className="text-xs text-blue-700 space-y-1 ml-4 list-decimal">
                      <li>Create a bot via <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="underline">@BotFather</a></li>
                      <li>Copy the Bot Token and paste above</li>
                      <li>Send a message to your bot</li>
                      <li>Get your Chat ID from <a href="https://t.me/userinfobot" target="_blank" rel="noopener noreferrer" className="underline">@userinfobot</a></li>
                      <li>Paste your Chat ID above</li>
                    </ol>
                  </div>
                  
                  {settings.telegram_bot_token && settings.telegram_chat_id && (
                    <button
                      onClick={async () => {
                        try {
                          const response = await fetch(`${API_URL}/admin/test-telegram`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${token}`,
                            },
                          });
                          
                          const data = await response.json();
                          
                          if (data.success) {
                            toast.success('✅ Test message sent! Check your Telegram.');
                          } else {
                            toast.error(data.error || 'Failed to send test message');
                          }
                        } catch (error) {
                          toast.error('Failed to send test message');
                        }
                      }}
                      className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                    >
                      <MessageCircle className="w-5 h-5" />
                      Send Test Message
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Branding Settings - Logo */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <ImageIcon className="w-6 h-6 text-blue-600" />
              <h3 className="text-xl font-bold text-gray-900">Branding & Logo</h3>
            </div>
            
            {/* Live Preview - How it will look in Navbar */}
            <div className="bg-gray-900 rounded-xl p-4 mb-4">
              <p className="text-gray-400 text-xs mb-2">📍 ตัวอย่าง Header (Live Preview)</p>
              <div className="flex items-center gap-3">
                {settings.logo_url ? (
                  <img 
                    src={settings.logo_url} 
                    alt="Logo Icon" 
                    className="h-10 w-auto object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ImageIcon className="w-6 h-6 text-white" />
                  </div>
                )}
                <span className="text-white text-xl font-bold">
                  {settings.logo_text || settings.site_name || 'HitzLink'}
                </span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-dashed border-blue-200 rounded-xl p-6">
              <div className="flex flex-col md:flex-row gap-6 items-start">
                {/* Logo Icon Preview */}
                <div className="flex-shrink-0">
                  <p className="text-sm font-medium text-gray-600 mb-2 text-center">Logo Icon</p>
                  {settings.logo_url ? (
                    <div className="w-32 h-32 rounded-lg border-2 border-blue-200 bg-white flex items-center justify-center overflow-hidden p-3">
                      <img 
                        src={settings.logo_url} 
                        alt="Logo Icon" 
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 24 24" fill="none" stroke="%233b82f6" stroke-width="2"%3E%3Crect x="3" y="3" width="18" height="18" rx="2"%3E%3C/rect%3E%3Cpath d="M9 11l3 3L22 4"/%3E%3C/svg%3E';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-32 h-32 rounded-lg border-2 border-dashed border-blue-300 bg-white flex items-center justify-center">
                      <ImageIcon className="w-16 h-16 text-blue-300" />
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-2 text-center">อัปโหลดเฉพาะ Icon</p>
                </div>
                
                {/* Upload Controls */}
                <div className="flex-1 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      🖼️ Logo Icon URL <span className="text-xs text-gray-500">(รูปภาพเท่านั้น ไม่รวม text)</span>
                    </label>
                    <input
                      type="text"
                      value={settings.logo_url}
                      onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
                      className="w-full px-4 py-2 border border-blue-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="https://example.com/logo-icon.png or upload below"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ✏️ Logo Text <span className="text-xs text-gray-500">(ข้อความที่แสดงข้างๆ icon)</span>
                    </label>
                    <input
                      type="text"
                      value={settings.logo_text}
                      onChange={(e) => setSettings({ ...settings, logo_text: e.target.value })}
                      className="w-full px-4 py-2 border border-blue-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="HitzLink (หรือปล่อยว่างจะใช้ Site Name)"
                    />
                  </div>
                  
                  {/* Upload Button */}
                  <div className="flex items-center gap-2">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={isUploadingLogo}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {isUploadingLogo ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          Upload Logo Icon
                        </>
                      )}
                    </button>
                    <span className="text-xs text-gray-600">
                      PNG, JPG, SVG (Max 5MB)
                    </span>
                  </div>
                  
                  <p className="text-xs text-blue-600">
                    💡 <strong>Tip:</strong> อัปโหลดเฉพาะ Logo Icon (ไม่รวม text) แนะนำขนาด 40x40px หรือ 50x50px พื้นหลังโปร่งใส PNG/SVG
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* SEO & Analytics — moved to /admin/seo */}
        </div>

        {/* Save Button (Bottom) */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
          >
            {isSaving ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Saving Changes...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Save All Settings
              </>
            )}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
};